import { cloneDeep } from "lodash";

import {
	isBool,
	isNonEmptyArray,
	isNonEmptyObject,
	isNonEmptyString,
} from "@cayuse/shared-react.utils.type-checks";

import { isValueUnique, notifyError, notifySuccess } from "utils";
import store, { dispatch } from "store/store";
import { IS_DEV } from "globals/constants";
import { tenant as constants } from "store/constants";
import { actionFactoryV2 } from "store/factories/fetchable";
import { camelToKebabCase, kebabToCamelCase } from "utils/helpers";
import { api } from "services";

const { setData, setError } = actionFactoryV2(constants, constants.TENANT);

export const setTenantData = setData;

const getTenant = () => cloneDeep(store.getState().tenant);
const isValidListItem = listItem => isNonEmptyString(listItem?.value) && isBool(listItem?.active);
const isListItemValueUnique = (array, value) => isValueUnique(array.map(listItem => listItem.value), value, "string");

const normalizeAndSetErrorMessage = (error, dictionaryKey) => {
	const message = IS_DEV ? error.message : store.getState().dictionary.common.tenant[dictionaryKey];

	notifyError(message, error);
	setError(message);
};

export const addListItem = async (listItem, list, listInstance = null) => {
	setError("");

	const tenant = getTenant();
	const previousListItems = tenant[list][listInstance];
	tenant[list][listInstance].push(listItem);

	if (!isValidListItem(listItem) && !isListItemValueUnique(tenant[list][listInstance], listItem)) {
		setError(`New list item for ${list} failed validation`);

		return;
	}

	setData(tenant);

	try {
		let response;

		if (listInstance !== null && list === "resolutions") {
			response = await api.tenant.saveByPath(
				{ tenantId: tenant.tenantId, id: camelToKebabCase(list), path: listInstance.toLowerCase() + "s" },
				listItem,
			);
		} else {
			let path;

			if (listInstance === "modifications") {
				path = list === "subawardModificationTypes" ? "modification-types/subawards" : "modification-types";
			} else {
				path = camelToKebabCase(list);
			}

			response = await api.tenant.save(
				{ tenantId: tenant.tenantId, id: path },
				listItem,
			);
		}

		if (!response) {
			tenant[list] = previousListItems;
			setData(tenant);
			setError(`Unable to update Tenant ${list}.`);
		}

	} catch (error) {
		normalizeAndSetErrorMessage(error, 'entryNotSaved');

		tenant[list] = previousListItems;
		setData(tenant);
	}
};

export const updateCategories = async (editedList, list, listInstance = null) => {
	const tenant = getTenant();

	try {
		await api.tenant.update(
			{ tenantId: tenant.tenantId, id: list },
			{ [listInstance]: editedList },
		);

		setData({
			[list]: {
				...tenant[list],
				[listInstance]: editedList,
			},
		});

	} catch (error) {
		normalizeAndSetErrorMessage(error, 'entryNotSaved');
	}
};

export const updateSubawardCap = async subawardCostCap => {
	setError("");

	const tenant = getTenant();

	try {
		await api.tenant.updateSubawardCostcap({ tenantId: tenant.tenantId }, subawardCostCap);

		setData({ subawardCostCap });

	} catch (error) {
		normalizeAndSetErrorMessage(error, 'entryNotSaved');
	}
};

export const toggleListItemActive = async (hash, list, listInstance) => {
	setError("");

	const tenant = getTenant();

	const previousListItems = (list === "resolutions") ? tenant[list] : tenant[list][listInstance];
	const modifiedList = (list === "resolutions") ? tenant[list] : tenant[list][listInstance];

	if (isNonEmptyObject(tenant[list])) {
		for (let itemInstance in tenant[list]) {
			tenant[list][itemInstance].forEach(item => {
				if (item.hash === hash && itemInstance === listInstance) {
					item.active = !item.active;
				}
			});
		}
	}

	setData(tenant);

	try {
		let path;

		if (listInstance === "modifications") {
			path = list === "subawardModificationTypes" ? "modification-types/subawards" : "modification-types";
		} else {
			path = camelToKebabCase(list);
		}

		const response = await api.tenant.update(
			{ tenantId: tenant.tenantId, id: path },
			modifiedList,
		);

		if (!response) {
			setError(`Unable to update Tenant ${list}.`);

			tenant[list] = previousListItems;
			setData(tenant);
		}

	} catch (error) {
		normalizeAndSetErrorMessage(error, 'changesNotSaved');

		tenant[list] = previousListItems;
		setData(tenant);
	}
};

export const toggleProjectAttachmentTypeActive = async (value, list) => {
	setError("");

	const tenant = getTenant();
	const previousListItems = tenant[list];

	if (isNonEmptyArray(tenant[list])) {
		tenant[list] = tenant[list].map(item => ({
			...item,
			active: item.value === value ? !item.active : item.active,
		}));
	}

	const modifiedList = tenant[list];
	setData(tenant);

	try {
		const response = await api.tenant.update(
			{ tenantId: tenant.tenantId, id: camelToKebabCase(list) },
			modifiedList,
		);

		if (!response) {
			setError(`Unable to update Tenant ${list}.`);

			tenant[list] = previousListItems;
			setData(tenant);
		}
	} catch (error) {
		normalizeAndSetErrorMessage(error, 'changesNotSaved');

		tenant[list] = previousListItems;
		setData(tenant);
	}
};

export const addProjectAttachmentType = async (listItem, list) => {
	setError("");

	const tenant = getTenant();

	if (!isValidListItem(listItem) && !isListItemValueUnique(tenant[list], listItem)) {
		setError(`New list item for ${list} failed validation`);

		return;
	}

	const previousListItems = tenant[list];
	tenant[list].push(listItem);

	setData(tenant);

	try {
		const response = await api.tenant.save(
			{ tenantId: tenant.tenantId, id: camelToKebabCase(list) },
			listItem,
		);

		if (!response) {
			tenant[list] = previousListItems;
			setData(tenant);
			setError(`Unable to update Tenant ${list}.`);
		}
	} catch (error) {
		normalizeAndSetErrorMessage(error, 'entryNotSaved');

		tenant[list] = previousListItems;
		setData(tenant);
	}
};

export const saveCertificationText = async (certificationText, formType) => {
	const { tenantId } = getTenant();

	const response = await api.tenant.saveCertificationText(
		{ tenantId, type: formType.toLowerCase() },
		{ certificationText, tenantId },
	).then(res => {
		notifySuccess(
			`Successfully updated certification text!`,
		);

		return res;
	}).catch(error => {
		notifyError("Something went wrong. Please try again.", error);

		return error;
	});

	dispatch({
		type: constants.SET_CERTIFICATION_TEXT,
		payload: {
			formType,
			certificationTextData: response,
		},
	})
};
